import { useCallback, useRef, useState } from "react";
import { ApiError } from "@/lib/api";

export interface AsyncAction<A extends unknown[], R> {
  run: (...args: A) => Promise<R | undefined>;
  loading: boolean;
  error: string | null;
  limpiarError: () => void;
}

/**
 * Envuelve una acción async (guardar, borrar, login…) con loading y mensaje de error.
 * Ignora dobles clicks mientras está corriendo. Devuelve undefined si falló.
 */
export function useAsyncAction<A extends unknown[], R>(accion: (...args: A) => Promise<R>): AsyncAction<A, R> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enCurso = useRef(false);
  const accionRef = useRef(accion);
  accionRef.current = accion;

  const run = useCallback(async (...args: A) => {
    if (enCurso.current) return undefined;
    enCurso.current = true;
    setLoading(true);
    setError(null);
    try {
      return await accionRef.current(...args);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Ocurrió un error inesperado");
      return undefined;
    } finally {
      enCurso.current = false;
      setLoading(false);
    }
  }, []);

  const limpiarError = useCallback(() => setError(null), []);
  return { run, loading, error, limpiarError };
}
